/* InstallPwaButton — shows a "📱 Install app" entry only when the browser
 * supports PWA install (Chrome / Edge / Android). On iOS Safari, where the
 * `beforeinstallprompt` event is not supported, falls back to an instructional
 * tooltip ("Tap the share button → Add to Home Screen").
 *
 * Hides itself entirely once the app is already installed (display-mode:
 * standalone) or after the user dismisses it once (24h cooldown).
 */
import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Stores the timestamp (ms) of the last "Not now" tap on the install banner
// so we don't nag the user every page load. Non-sensitive UX preference —
// auth tokens never touch localStorage (httpOnly `clc_session` cookie only).
const COOLDOWN_KEY = "clc.pwa-install.dismissed-at";
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** Stamp the current time so the install banner stays hidden for ~24h.
 *  Failures are non-fatal (Safari Private mode throws on localStorage.set);
 *  worst case the banner reappears on the next visit. */
function stampDismissalCooldown() {
  try {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  } catch (err) {
    // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
    if (typeof console !== "undefined") console.warn("PWA cooldown stamp skipped (private browsing?):", err?.message || err);
  }
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(COOLDOWN_KEY) || "0");
    return ts && Date.now() - ts < COOLDOWN_MS;
  } catch (err) {
    // Same private-browsing guard as above; treat as "not dismissed" so
    // the banner can render.
    // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
    if (typeof console !== "undefined") console.warn("PWA cooldown read skipped (private browsing?):", err?.message || err);
    return false;
  }
}

function isStandalone() {
  return (
    (typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function isiOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function InstallPwaButton({ variant = "ghost", className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setAvailable(true);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt — show the helper button anyway
    // so the user can see how to add it. Cooldown after dismissal.
    if (isiOS() && !recentlyDismissed()) setAvailable(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !available) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") {
        stampDismissalCooldown();
      }
      setDeferred(null);
      setAvailable(false);
    } else if (isiOS()) {
      setShowIosHint(true);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size="sm"
        onClick={click}
        data-testid="pwa-install-btn"
        className={`gap-2 ${className}`}
      >
        <Smartphone className="h-4 w-4" /> Install app
      </Button>

      <Dialog open={showIosHint} onOpenChange={setShowIosHint}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#C9A84C]" /> Install on iPhone / iPad
            </DialogTitle>
          </DialogHeader>
          <ol className="text-sm space-y-2 text-[#1A2744]">
            <li><strong>1.</strong> Tap the <span className="px-1.5 py-0.5 bg-gray-100 rounded">Share</span> button at the bottom of Safari.</li>
            <li><strong>2.</strong> Scroll down and tap <strong>Add to Home Screen</strong>.</li>
            <li><strong>3.</strong> Tap <strong>Add</strong> in the top-right.</li>
          </ol>
          <p className="text-xs text-gray-500">The portal icon will appear on your home screen and open in fullscreen, just like a native app.</p>
          <Button
            data-testid="pwa-install-ios-dismiss"
            onClick={() => {
              stampDismissalCooldown();
              setShowIosHint(false);
              setAvailable(false);
            }}
            className="bg-[#1A2744] hover:bg-[#0F1A30]"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
